import React from 'react';
import clsx from 'clsx';

import './SupplierActivityFilterSearchPanel.css';
import {
  INVITED_EVENT_COUNT_MAX,
  SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID,
  SupplierActivityFilterTreeNodeId,
  supplierActivityFilterRootTreeNode
} from 'constants/filter';
import { noop } from 'util/function';
import { TransformedFilterTreeNode } from 'components/tree/FilterTree';
import CompactRangeSlider, { CompactRangeSliderProps } from 'components/slider/CompactRangeSlider';
import FilterSearchPanel, { FilterSearchPanelProps } from 'components/panel/filter/FilterSearchPanel';

//
// --- Types ---

export interface SupplierActivityFilterSearchPanelProps {
  className?: string;
  'data-testid'?: string;
  invitedEventCountRangeEnd: number;
  invitedEventCountRangeStart: number;
  invitedPortalCountRangeEnd: number;
  invitedPortalCountRangeStart: number;
  onInvitedEventRangeChange?: (rangeStart: number, rangeEnd: number) => void;
  onInvitedPortalRangeChange?: (rangeStart: number, rangeEnd: number) => void;
  render: boolean;
}

interface SupplierActivityRangeSliderProps
  extends Omit<CompactRangeSliderProps, 'getAriaLabel' | 'max' | 'min' | 'onChange' | 'onChangeCommitted' | 'value'> {
  /** a description label used in each sliders generated aria label */
  ariaEntityLabel: string;
  className?: string;
  'data-testid'?: string;
  end: number;
  onRangeChange: (start: number, end: number) => void;
  start: number;
}

type TreeNodeComponentMap = Partial<Record<SupplierActivityFilterTreeNodeId, React.ReactNode>>;

//
// --- Helper Functions ---
const getTreeNodesWithComponentReplacement = (
  treeNode: TransformedFilterTreeNode,
  componentMap: TreeNodeComponentMap
): TransformedFilterTreeNode => {
  const treeNodeId = treeNode.id as SupplierActivityFilterTreeNodeId;
  const component = componentMap[treeNodeId] ? componentMap[treeNodeId] : undefined;
  const updatedTreeNode = component ? { ...treeNode, component } : treeNode;
  return Array.isArray(treeNode.children) && treeNode.children.length
    ? {
        ...updatedTreeNode,
        children: treeNode.children.map(childNode => getTreeNodesWithComponentReplacement(childNode, componentMap))
      }
    : updatedTreeNode;
};

//
// --- Helper Components ---

const SupplierActivityRangeSlider: React.FC<SupplierActivityRangeSliderProps> = props => {
  const { ariaEntityLabel, className, 'data-testid': dataTestId = 'activity-range', end, start, onRangeChange } = props;

  const [value, setValue] = React.useState<number | number[]>([start, end]);
  const lastCommittedRangeRef = React.useRef<{ end: number; start: number }>({ end: end, start: start });

  React.useEffect(() => {
    setValue([start, end]);
    lastCommittedRangeRef.current = { end, start };
  }, [end, start]);

  const handleSliderChange: CompactRangeSliderProps['onChange'] = (event, newValue) => {
    setValue(newValue as number[]);
  };

  const handleSliderChangeCommitted: CompactRangeSliderProps['onChangeCommitted'] = (event, newValue) => {
    const [start, end] = Array.isArray(newValue) ? newValue : [newValue, newValue];

    // on trigger change start/end are not the same as the last value
    if (lastCommittedRangeRef.current.start !== start || lastCommittedRangeRef.current.end !== end) {
      lastCommittedRangeRef.current = { end, start };
      onRangeChange(start, end);
    }
  };

  return (
    <div className={clsx('supplier-activity-filter-search-panel-supplier-activity-range-slider', className)}>
      <CompactRangeSlider
        data-testid={dataTestId}
        getAriaLabel={index => `${index === 0 ? 'minimum' : 'maximum'} ${ariaEntityLabel} slider`}
        max={INVITED_EVENT_COUNT_MAX}
        min={0}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        value={value}
        valueLabelDisplay="on"
        valueLabelFormat={(value, index) => (index === 1 && value === INVITED_EVENT_COUNT_MAX ? `${value}+` : value)}
        marks
      />
    </div>
  );
};

//
// --- SupplierActivityFilterSearchPanel Component ---

const SupplierActivityFilterSearchPanel: React.FC<SupplierActivityFilterSearchPanelProps> = props => {
  const {
    className,
    'data-testid': dataTestId = 'supplier-activity-filter-search-panel',
    invitedEventCountRangeEnd,
    invitedEventCountRangeStart,
    invitedPortalCountRangeEnd,
    invitedPortalCountRangeStart,
    onInvitedEventRangeChange = noop,
    onInvitedPortalRangeChange = noop,
    render
  } = props;

  const [openTreeNodeIds, setOpenTreeNodeIds] = React.useState<ReadonlyArray<string>>([
    SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_COUNT,
    SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_COUNT
  ]);

  const handleFilterOpenStateChange: FilterSearchPanelProps['onFilterOpenStateChange'] = (openFilterId, isOpen) => {
    setOpenTreeNodeIds(isOpen ? [...openTreeNodeIds, openFilterId] : openTreeNodeIds.filter(id => id !== openFilterId));
  };

  const treeData = [
    getTreeNodesWithComponentReplacement(supplierActivityFilterRootTreeNode, {
      [SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_EVENT_RANGE]: (
        <SupplierActivityRangeSlider
          ariaEntityLabel="invited event count"
          className="invited-event-slider"
          data-testid="events-compact-range-slider"
          end={invitedEventCountRangeEnd}
          onRangeChange={onInvitedEventRangeChange}
          start={invitedEventCountRangeStart}
        />
      ),
      [SUPPLIER_ACTIVITY_FILTER_TREE_NODE_ID.INVITED_PORTAL_RANGE]: (
        <SupplierActivityRangeSlider
          ariaEntityLabel="invited portal count"
          className="invited-portal-slider"
          data-testid="portals-compact-range-slider"
          end={invitedPortalCountRangeEnd}
          onRangeChange={onInvitedPortalRangeChange}
          start={invitedPortalCountRangeStart}
        />
      )
    })
  ];

  return (
    <FilterSearchPanel
      className={clsx('supplier-activity-filter-search-panel', className)}
      data-testid={dataTestId}
      filterTreeData={treeData}
      onFilterOpenStateChange={handleFilterOpenStateChange}
      openFilterIds={openTreeNodeIds}
      render={render}
    />
  );
};

export default SupplierActivityFilterSearchPanel;
